import React from 'react';
import { AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';

export interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'success' | 'info';
  loading?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  loading = false,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const getTypeConfig = () => {
    switch (type) {
      case 'danger':
        return {
          headerBg: 'from-slate-800 to-slate-900',
          icon: AlertTriangle,
          iconColor: 'text-red-500',
          accentBg: 'bg-red-50',
          accentBorder: 'border-red-200',
          buttonBg: 'bg-red-600 hover:bg-red-700',
          buttonRing: 'focus:ring-red-500',
        };
      case 'warning':
        return {
          headerBg: 'from-slate-800 to-slate-900',
          icon: AlertCircle,
          iconColor: 'text-amber-500',
          accentBg: 'bg-amber-50',
          accentBorder: 'border-amber-200',
          buttonBg: 'bg-amber-600 hover:bg-amber-700',
          buttonRing: 'focus:ring-amber-500',
        };
      case 'success':
        return {
          headerBg: 'from-slate-800 to-slate-900',
          icon: CheckCircle,
          iconColor: 'text-emerald-500',
          accentBg: 'bg-emerald-50',
          accentBorder: 'border-emerald-200',
          buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
          buttonRing: 'focus:ring-emerald-500',
        };
      default:
        return {
          headerBg: 'from-slate-800 to-slate-900',
          icon: Info,
          iconColor: 'text-blue-500',
          accentBg: 'bg-blue-50',
          accentBorder: 'border-blue-200',
          buttonBg: 'bg-blue-600 hover:bg-blue-700',
          buttonRing: 'focus:ring-blue-500',
        };
    }
  };

  const config = getTypeConfig();
  const IconComponent = config.icon;

  return (
    <>
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.headerBg} px-8 py-6 rounded-t-2xl`}>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-xl">
            <IconComponent className="text-white" size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">{title}</h3>
            <p className="text-slate-300 text-sm mt-1">Please confirm your action</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-8 py-6 bg-white">
        <div className={`flex items-start gap-3 ${config.accentBg} ${config.accentBorder} border rounded-xl px-5 py-4`}>
          <IconComponent className={`${config.iconColor} mt-0.5 flex-shrink-0`} size={20} />
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{message}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-8 py-5 bg-slate-50 rounded-b-2xl border-t border-slate-200 flex justify-end gap-3">
        <button
          onClick={onClose || onCancel}
          disabled={loading}
          className="px-5 py-2.5 bg-white text-slate-700 font-medium rounded-xl border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all disabled:opacity-50 text-sm"
        >
          {cancelText}
        </button>
        <button
          onClick={async () => {
            try {
              await onConfirm();
            } finally {
              onClose?.();
            }
          }}
          disabled={loading}
          className={`inline-flex items-center gap-2 px-5 py-2.5 ${config.buttonBg} text-white font-medium rounded-xl focus:outline-none focus:ring-2 ${config.buttonRing} focus:ring-offset-2 transition-all shadow-sm disabled:opacity-50 text-sm`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Processing...
            </>
          ) : (
            confirmText
          )}
        </button>
      </div>
    </>
  );
};

export default ConfirmModal;
